import { createSystem, defineConfig, themes } from "shared/components/Spor";
import { DarkTheme, darkTheme } from "shared/theme/darkTheme";
import { LightTheme, lightTheme } from "shared/theme/lightTheme";
import { extendSidespor } from "shared/theme/sidespor";
import { buttonHover } from "shared/theme/style/buttonHover";
import vyTeknologiTokens from "shared/theme/colors/vy-teknologi";

export type ColorTheme = DarkTheme & LightTheme;

// Adds a theme color to systemConfig. E.g background: mode((theme) => theme.colorSuccess)
const mode = (styleFunction: (colorTheme: ColorTheme) => string) => ({
  _dark: styleFunction(darkTheme),
  _light: styleFunction(lightTheme),
});
type SystemConfig = ReturnType<typeof defineConfig>;

const systemConfig: SystemConfig = {
  theme: {
    recipes: {
      /**
       * Centers the text inside buttons
       */
      button: {
        base: {
          lineHeight: 1,
        },
      },
      /**
       * Spor textarea recipe ships without horizontal padding (no size
       * variants, unlike input), leaving the text flush against the edge.
       * Restore it to match the input recipe's md padding.
       */
      textarea: {
        base: {
          paddingX: 3,
        },
      },
      /**
       * Remove these when Spor is updated and these values are live
       */
      badge: {
        variants: {
          size: {
            md: {
              fontFamily: "body",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "21px",
              letterSpacing: "0",
              textAlign: "center",
            },
          },
        },
      },
    },
    slotRecipes: {
      /**
       * Add missing gap between icon and content for <FilterChip/> and fix height
       */
      checkboxCard: {
        slots: [],
        base: {
          root: {
            maxHeight: "33px !important",
          },
          label: {
            gap: "3px",
          },
        },
      },
      /**
       * Makes the trigger button squared and with buttonHover and adds gap between button and input
       */
      datePicker: {
        slots: [],
        base: {
          calendarTriggerButton: {
            borderRadius: "6px",
            padding: "6px !important",
            margin: "-3px",
            ...buttonHover,
          },
          wrapper: {
            gap: "1%", // A nice workaround to add a bigger gap for DateRangePicker than DatePicker, as DateRangePicker had less originally
          },
        },
      },
      /**
       * Adds drops related background colors for table
       */
      table: {
        slots: [],
        base: {
          root: {
            background: "transparent !important", // Spor uses colorBackgroundBase which is incorrect
          },
          row: {
            border: mode((_) => "none !important"), // "Oversikt" needs this
            borderBlockEnd: mode(
              (theme) =>
                `1px solid ${theme.colorBackgroundSecondary} !important`,
            ), // "TrainRoute" needs this
          },
        },
      },
      /**
       * Adds drops related background colors for tabs
       */
      tabs: {
        slots: [],
        base: {
          trigger: {
            transition: "background 0.25s",
          },
        },
      },
      /**
       * Fix sizes to match previous Spor and add drops related background colors
       */
      dialog: {
        slots: [],
        base: {
          content: {
            backgroundColor: mode((theme) => theme.colorBackgroundMain),
            border: mode((theme) => `1px solid ${theme.colorOutlineSecondary}`),
          },
          body: {
            backgroundColor: mode((theme) => theme.colorBackgroundSecondary),
            borderRadius: "18px",
            margin: "0px 6px 6px 6px", // No margin top as we often uses tabs there
            padding: "18px 36px",
            border: mode((theme) => `1px solid ${theme.colorOutlineSecondary}`),
          },
        },
        variants: {
          size: {
            lg: {
              content: {
                minWidth: "575px", // from 670px
                maxWidth: "575px", // from 670px
              },
            },
            xl: {
              content: {
                minWidth: "1020px", // from 900px
                maxWidth: "1020px", // from 900px
              },
            },
          },
        },
      },
      /**
       * Fix nativeSelect background color
       */
      nativeSelect: {
        slots: [],
        base: {
          root: {
            backgroundColor: "transparent",
          },
        },
      },
    },
    /**
     * Remove these when Spor is updated and these values are live
     */
    semanticTokens: {
      colors: {
        bg: {
          brand: vyTeknologiTokens.color.vyUtvikling.bg.brand,
          subtle: vyTeknologiTokens.color.vyUtvikling.bg.subtle,
        },
        surface: {
          DEFAULT: vyTeknologiTokens.color.vyUtvikling.surface.DEFAULT,
        },
      },
    },
    /**
     * Remove these when Spor is updated and these values are live
     */
    textStyles: {
      md: {
        value: {
          fontWeight: 700,
          fontSize: "18px",
          lineHeight: "27px",
        },
      },
      lg: {
        value: {
          fontWeight: 700,
          fontSize: "24px",
          lineHeight: "36px",
        },
      },
    },
  },
};
const systemConfigWithSidespor = extendSidespor(systemConfig);
const config = defineConfig(systemConfigWithSidespor);
export const system = createSystem(themes.VyUtvikling._config, config);
